import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Calendar as CalIcon,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { BRAND, SERVICES } from "@/lib/constants";
import { trackView } from "@/lib/track";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Service", "Date & Time", "Your Details", "Confirmed"];

const formatDate = (d) =>
  d
    ? d.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

const toIsoDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function BookingPage() {
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 28);
    return d;
  }, [today]);

  // Fetch slots when date changes
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setSlotsLoading(true);
    setSlot(null);
    axios
      .get(`${API}/bookings/availability/${toIsoDate(date)}`)
      .then((res) => {
        if (cancelled) return;
        setSlots(res.data?.slots || []);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Could not load availability");
          setSlots([]);
        }
      })
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [date]);

  const goNext = () => {
    if (step === 0 && !service) return toast.error("Please choose a service.");
    if (step === 1 && (!date || !slot)) return toast.error("Pick a date and time slot.");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!details.name || !details.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        name: details.name,
        email: details.email,
        phone: details.phone || null,
        service: service.title,
        date: toIsoDate(date),
        start_time: slot.start_time,
        notes: details.notes || null,
      });
      setConfirmation(res.data);
      setStep(3);
      toast.success("Session booked. See you then!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed.");
      if (err?.response?.status === 409) {
        // Refresh slots — slot was just taken
        if (date) {
          const r = await axios.get(`${API}/bookings/availability/${toIsoDate(date)}`);
          setSlots(r.data?.slots || []);
          setSlot(null);
          setStep(1);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (d) => {
    const dd = new Date(d);
    dd.setHours(0, 0, 0, 0);
    if (dd < today) return true;
    if (dd > maxDate) return true;
    const dow = dd.getDay(); // 0=Sun..6=Sat
    if (dow === 0 || dow === 6) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-body">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/85 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="booking-back-home"
            className="inline-flex items-center gap-3 group"
          >
            <img src={BRAND.logo} alt="JE Training" className="h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-400 hover:text-white transition-colors"
            data-testid="booking-close"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to site
          </Link>
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.28em] uppercase">
            Book A Session
          </p>
        </div>
        <h1
          data-testid="booking-heading"
          className="font-display text-white font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl"
        >
          Lock in <span className="text-[#D4AF37]">your slot.</span>
        </h1>
        <p className="mt-4 text-neutral-400 text-base max-w-xl">
          Choose a service, pick a time and you're in. Sessions run Mon–Fri, 6am–9pm at the private studio in Braintree.
        </p>

        {/* Stepper bar */}
        <ol
          className="mt-12 grid grid-cols-4 gap-2 lg:gap-4 mb-12"
          data-testid="booking-stepper"
        >
          {STEPS.map((label, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <li key={label} className="flex flex-col gap-2">
                <div
                  className={`h-1 w-full ${
                    done || active ? "bg-[#D4AF37]" : "bg-white/10"
                  }`}
                />
                <span
                  className={`text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] ${
                    active ? "text-white" : done ? "text-[#D4AF37]" : "text-neutral-500"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} · {label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Step content */}
      <main className="max-w-5xl mx-auto px-6 lg:px-8 pb-24">
        {step === 0 && (
          <section data-testid="booking-step-service">
            <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6">
              Pick a service
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SERVICES.map((s) => {
                const selected = service?.id === s.id;
                return (
                  <button
                    type="button"
                    key={s.id}
                    data-testid={`booking-service-${s.id}`}
                    onClick={() => setService(s)}
                    className={`text-left p-6 lg:p-7 border bg-[#0A0A0A] transition-all hover:-translate-y-0.5 ${
                      selected
                        ? "border-[#D4AF37] ring-1 ring-[#D4AF37]"
                        : "border-white/5 hover:border-[#D4AF37]/40"
                    }`}
                  >
                    <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.3em] uppercase mb-2">
                      {s.short}
                    </p>
                    <h3 className="font-display text-xl lg:text-2xl font-bold uppercase tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-neutral-400 text-sm font-light leading-relaxed">
                      {s.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 1 && (
          <section
            data-testid="booking-step-datetime"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            <div className="lg:col-span-6">
              <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6">
                Choose a date
              </h2>
              <div className="bg-[#0A0A0A] border border-white/5 p-3 inline-block">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  fromDate={today}
                  toDate={maxDate}
                  weekStartsOn={1}
                  showOutsideDays={false}
                  data-testid="booking-calendar"
                  className="bg-[#0A0A0A] text-white [&_button]:rounded-none"
                />
              </div>
              <p className="mt-3 text-neutral-500 text-xs">
                Mon–Fri only · up to 4 weeks ahead
              </p>
            </div>
            <div className="lg:col-span-6">
              <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6">
                Available times
              </h2>
              {!date ? (
                <p className="text-neutral-500 text-sm">Pick a date to see open slots.</p>
              ) : slotsLoading ? (
                <div className="flex items-center gap-2 text-neutral-400 text-sm">
                  <Loader2 size={16} className="animate-spin" /> Loading…
                </div>
              ) : (
                <>
                  <p className="text-neutral-400 text-sm mb-4">{formatDate(date)}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((s) => {
                      const selected = slot?.start_time === s.start_time;
                      return (
                        <button
                          type="button"
                          key={s.start_time}
                          data-testid={`booking-slot-${s.start_time}`}
                          disabled={!s.available}
                          onClick={() => setSlot(s)}
                          className={`px-3 py-3 text-sm font-bold tracking-wide border transition-colors ${
                            selected
                              ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                              : s.available
                              ? "bg-[#0A0A0A] border-white/10 text-white hover:border-[#D4AF37]"
                              : "bg-[#0A0A0A] border-white/5 text-neutral-700 line-through cursor-not-allowed"
                          }`}
                        >
                          {s.start_time}
                        </button>
                      );
                    })}
                  </div>
                  {slots.length > 0 && slots.every((s) => !s.available) && (
                    <p className="mt-4 text-neutral-500 text-xs">
                      No slots free on this day — try another date.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {step === 2 && (
          <section
            data-testid="booking-step-details"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            <div className="lg:col-span-7">
              <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight mb-6">
                Your details
              </h2>
              <div className="bg-[#0A0A0A] border border-white/5 p-6 lg:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-400">Name</Label>
                  <Input
                    data-testid="booking-input-name"
                    value={details.name}
                    onChange={(e) => setDetails({ ...details, name: e.target.value })}
                    placeholder="Full name"
                    className="mt-2 rounded-none bg-[#050505] border-white/10 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-12"
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-400">Email</Label>
                  <Input
                    type="email"
                    data-testid="booking-input-email"
                    value={details.email}
                    onChange={(e) => setDetails({ ...details, email: e.target.value })}
                    placeholder="you@example.com"
                    className="mt-2 rounded-none bg-[#050505] border-white/10 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-12"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-400">Phone</Label>
                  <Input
                    data-testid="booking-input-phone"
                    value={details.phone}
                    onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                    placeholder="Optional"
                    className="mt-2 rounded-none bg-[#050505] border-white/10 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white h-12"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] font-bold tracking-[0.22em] uppercase text-neutral-400">Anything I should know?</Label>
                  <Textarea
                    rows={4}
                    data-testid="booking-input-notes"
                    value={details.notes}
                    onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                    placeholder="Goals, injuries, experience level…"
                    className="mt-2 rounded-none bg-[#050505] border-white/10 focus-visible:border-[#D4AF37] focus-visible:ring-0 text-white"
                  />
                </div>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <h3 className="text-[10px] font-bold tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Booking Summary</h3>
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 p-6 space-y-4 text-sm">
                <SummaryRow icon={CalIcon} label="Date" value={formatDate(date)} />
                <SummaryRow icon={Clock} label="Time" value={slot ? `${slot.start_time}–${slot.end_time} (60 min)` : ""} />
                <SummaryRow icon={User} label="Service" value={service?.title} />
              </div>
              <p className="mt-4 text-neutral-500 text-xs">
                Auto-confirmed. You'll get an email confirmation; Jamie is also notified.
              </p>
            </aside>
          </section>
        )}

        {step === 3 && confirmation && (
          <section
            data-testid="booking-step-confirmed"
            className="text-center max-w-2xl mx-auto py-10"
          >
            <div className="inline-flex items-center justify-center h-20 w-20 border border-[#D4AF37] mb-8">
              <CheckCircle2 size={36} strokeWidth={1.5} className="text-[#D4AF37]" />
            </div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.28em] uppercase mb-4">
              Booking Confirmed
            </p>
            <h2 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              See you on the floor.
            </h2>
            <div className="mt-10 bg-[#0A0A0A] border border-white/5 p-8 text-left grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SummaryRow icon={CalIcon} label="Date" value={formatDate(new Date(confirmation.date))} />
              <SummaryRow icon={Clock} label="Time" value={`${confirmation.start_time}–${confirmation.end_time}`} />
              <SummaryRow icon={User} label="Service" value={confirmation.service} />
              <SummaryRow icon={Mail} label="Confirmation to" value={confirmation.email} />
              <SummaryRow icon={Phone} label="Phone" value={confirmation.phone || "—"} />
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                data-testid="booking-confirmed-home"
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D4AF37] text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={1.5} /> Back to site
              </Link>
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-confirmed-ig"
                className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C865] text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] transition-colors"
              >
                Follow on Instagram
              </a>
            </div>
          </section>
        )}

        {/* Step nav */}
        {step < 3 && (
          <div className="mt-12 flex items-center justify-between">
            <button
              type="button"
              onClick={goBack}
              disabled={step === 0}
              data-testid="booking-back-button"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-neutral-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> Back
            </button>

            {step < 2 ? (
              <button
                type="button"
                onClick={goNext}
                data-testid="booking-next-button"
                className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C865] text-black px-7 py-4 text-xs font-bold uppercase tracking-[0.22em] transition-colors"
              >
                Continue <ArrowRight size={14} strokeWidth={2} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="booking-submit-button"
                className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C865] disabled:opacity-60 text-black px-7 py-4 text-xs font-bold uppercase tracking-[0.22em] transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Booking…
                  </>
                ) : (
                  <>
                    Confirm Booking <CheckCircle2 size={14} strokeWidth={2} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const SummaryRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="h-9 w-9 border border-white/10 flex items-center justify-center shrink-0">
      <Icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
    </div>
    <div>
      <p className="text-neutral-500 text-[10px] font-bold tracking-[0.25em] uppercase">{label}</p>
      <p className="text-white text-sm font-semibold mt-0.5">{value || "—"}</p>
    </div>
  </div>
);
